import { observer } from 'mobx-react';
import React from 'react';
import ScrollableBlock from '../../ScrollableBlock/ScrollableBlock';
import CategoryAndProductModalItem from './components/CategoryAndProductModalItem/CategoryAndProductModalItem';
import CategoryAndProductModalHeader from './containers/CategoryAndProductModalHeader/CategoryAndProductModalHeader';
import { defaultPagination, PER_PAGE } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { TCategory } from 'src/types/categories';
import { IPagination, MODALS_CRUMB, TBooleanInNumber, TTranslate } from 'src/types/main';
import { IProductV4, PRODUCT_TYPE } from 'src/types/product';
import { emptyArray, getLastArrayItem, getUnavailable } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import logger from '~/services/logger';

export interface ICategoryOrProductItem {
  id: number;
  name: TTranslate;
  status: TBooleanInNumber;
  is_delete?: TBooleanInNumber;
  category_id?: number;
}

export enum CATEGORY_MODAL_SECTIONS {
  CATEGORIES = 'categories',
  ITEMS = 'items',
}

export enum CATEGORY_MODAL_CATEGORY {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  NONE = 'none',
}

export enum CATEGORY_MODAL_PRODUCT {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  NONE = 'none',
}

interface IState {
  section: CATEGORY_MODAL_SECTIONS;
  activeCrumb: MODALS_CRUMB;
  search: string;
  selectedCategories: ICategoryOrProductItem[];
  selectedProducts: ICategoryOrProductItem[];
  categoriesPagination: IPagination<TCategory>;
  productsPagination: IPagination<IProductV4>;
  isAllCategories: boolean;
  isAllProducts: boolean;
  isAllStatuses: boolean;
  categoriesWithProducts: boolean;
  refreshing: boolean;
  loading: boolean;
}

@observer
export default class CategoryAndProductModal extends React.Component<{}, IState> {
  selectedUnavailableCategories: ICategoryOrProductItem[] = modalStore.categoryAndProductModal?.selectedUnavailableCategories || [];
  selectedUnavailableProducts: ICategoryOrProductItem[] = modalStore.categoryAndProductModal?.selectedUnavailableProducts || [];
  state = {
    section: modalStore.categoryAndProductModal?.defaultSection || CATEGORY_MODAL_SECTIONS.CATEGORIES,
    activeCrumb: MODALS_CRUMB.ALL,
    search: '',
    selectedCategories: modalStore.categoryAndProductModal?.selectedCategories || [],
    selectedProducts: modalStore.categoryAndProductModal?.selectedProducts || [],
    categoriesPagination: defaultPagination,
    productsPagination: defaultPagination,
    isAllCategories: modalStore.categoryAndProductModal?.isAllCategories || false,
    isAllProducts: modalStore.categoryAndProductModal?.isAllProducts || false,
    isAllStatuses: modalStore.categoryAndProductModal?.isAllStatuses || false,
    categoriesWithProducts: !!modalStore.categoryAndProductModal?.categoriesWithProducts,
    refreshing: false,
    loading: false,
    types: modalStore.categoryAndProductModal?.productTypes || [PRODUCT_TYPE.PRODUCT],
  };

  componentDidMount = () => {
    if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES) {
      this.fetchCategory(1);
    } else {
      this.fetchProducts(1);
    }
  };

  close = () => {
    modalStore.closeCategoryAndProductModal();
  };

  clickSave = () => {
    modalStore.categoryAndProductModal?.onSave(this.state.selectedCategories, this.state.selectedProducts);
  };

  clickSection = (section: CATEGORY_MODAL_SECTIONS) => {
    this.setState({ section, activeCrumb: MODALS_CRUMB.ALL, search: '' }, () =>
      this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? this.fetchCategory(1) : this.fetchProducts(1)
    );
  };

  clickCrumb = (crumb: MODALS_CRUMB) => {
    this.setState({ activeCrumb: crumb }, () =>
      this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? this.fetchCategory(1) : this.fetchProducts(1)
    );
  };

  clickSelectAll = () => {
    if (this.state.search) return;
    if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES) {
      if (this.state.isAllCategories) return;
      this.setState({ isAllCategories: true, selectedProducts: [], isAllProducts: true }, () =>
        this.state.activeCrumb === MODALS_CRUMB.SELECTED ? this.fetchCategory(1) : null
      );
      this.fetchAndSelectAllCategories();
    } else {
      if (this.state.isAllProducts) return;
      this.setState({ isAllProducts: true }, () => (this.state.activeCrumb === MODALS_CRUMB.SELECTED ? this.fetchProducts(1) : null));
      this.fetchAndSelectAllProducts();
    }
  };

  clickDeselectAll = () => {
    if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES) {
      this.setState({ selectedCategories: [], isAllCategories: false, selectedProducts: [], isAllProducts: false });
    } else {
      this.setState({ selectedProducts: [], isAllProducts: false });
    }
  };

  changeLoading = (loading: boolean) => {
    this.setState({ loading });
  };

  fetchCategory = async (page: number = this.state.categoriesPagination.current_page + 1) => {
    try {
      if (this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.selectedCategories.length && !this.state.isAllCategories) return;
      this.setState({ loading: true });
      const categories: IPagination<TCategory> = await client.get(`/v3/categories`, {
        params: {
          ...this.getFetchingParams(),
          categories:
            this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAllCategories
              ? this.state.selectedCategories.map((category) => category.id)
              : undefined,
          page,
        },
      });
      this.setState({ categoriesPagination: categories, loading: false });
    } catch (error: any) {
      logger.error({ error, place: 'CategoryAndProductModal fetchCategory' });
      this.setState({ loading: false });
    }
  };

  fetchAndSelectAllCategories = async () => {
    try {
      this.setState({ refreshing: true });
      const categories: IPagination<TCategory> = await client.get('/v3/categories', {
        params: {
          ...this.getFetchingParams(),
          categories:
            this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAllCategories
              ? this.state.selectedCategories.map((category) => category.id)
              : undefined,
          per_page: PER_PAGE.MAXIMUM,
        },
      });
      this.setState({ selectedCategories: categories.data, refreshing: false });
    } catch (error: any) {
      logger.error({ error, place: 'CategoryAndProductModal fetchAndSelectAllCategories' });
      this.setState({ refreshing: false });
    }
  };

  changeCategory = (category?: ICategoryOrProductItem) => {
    if (!category) return;
    if (modalStore.categoryAndProductModal?.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE) {
      const productSelected: boolean = this.state.selectedProducts.length > 0;
      if (this.state.isAllCategories) {
        this.setState(
          {
            selectedCategories: [category],
            isAllCategories: false,
            selectedProducts: productSelected ? this.state.selectedProducts : [],
            isAllProducts: productSelected ? false : true,
          },
          () => (this.state.activeCrumb === MODALS_CRUMB.SELECTED ? this.clickCrumb(MODALS_CRUMB.ALL) : null)
        );
      } else if (this.state.selectedCategories.find((item) => item.id === category.id)) {
        this.setState({
          selectedCategories: this.state.selectedCategories.filter((item) => item.id !== category.id),
          selectedProducts: this.state.selectedProducts.filter((product) => product.category_id !== category.id),
        });
      } else {
        this.setState({
          selectedCategories: this.state.selectedCategories.concat(category),
          selectedProducts: productSelected ? this.state.selectedProducts : [],
          isAllProducts: productSelected ? false : true,
        });
      }
    } else {
      if (getLastArrayItem<ICategoryOrProductItem>(this.state.selectedCategories)?.id === category.id) return;
      this.setState({ selectedCategories: [category], selectedProducts: [] });
    }
  };

  fetchProducts = async (page: number = this.state.productsPagination.current_page + 1) => {
    try {
      if (
        (!this.state.selectedCategories.length && !this.state.isAllCategories) ||
        (this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.selectedCategories.length && !this.state.isAllProducts)
      ) {
        return;
      }
      this.setState({ loading: true });
      const products: IPagination<IProductV4> = await client.get(`/v4/products`, {
        params: {
          ...this.getFetchingParams(),
          types: this.state.types,
          categories: this.state.selectedCategories.map((category) => category.id),
          products:
            this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAllProducts
              ? this.state.selectedProducts?.map((product) => product.id)
              : undefined,
          page,
        },
      });
      this.setState({ productsPagination: products, loading: false });
    } catch (error: any) {
      logger.error({ error, place: 'CategoryAndProductModal fetchProducts' });
      this.setState({ loading: false });
    }
  };

  fetchAndSelectAllProducts = async () => {
    try {
      this.setState({ refreshing: true });

      const products: IPagination<IProductV4> = await client.get('/v4/products', {
        params: {
          ...this.getFetchingParams(),
          types: this.state.types,
          categories: this.state.selectedCategories.map((category) => category.id),
          products:
            this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAllProducts
              ? this.state.selectedProducts?.map((product) => product.id)
              : undefined,
          per_page: PER_PAGE.MAXIMUM,
        },
      });
      this.setState({ selectedProducts: products.data, refreshing: false });
    } catch (error: any) {
      logger.error({ error, place: 'CategoryAndProductModal fetchAndSelectAllProducts' });
      this.setState({ refreshing: false });
    }
  };

  changeProduct = (product?: ICategoryOrProductItem) => {
    if (!product) return;
    if (modalStore.categoryAndProductModal?.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE) {
      if (this.state.isAllProducts) {
        if (this.state.activeCrumb === MODALS_CRUMB.SELECTED) {
          this.setState({ selectedProducts: [product], isAllProducts: false }, () => this.clickCrumb(MODALS_CRUMB.ALL));
        } else {
          this.setState({ selectedProducts: [product], isAllProducts: false });
        }
      } else if (this.state.selectedProducts.find((item) => item.id === product.id)) {
        this.setState({ selectedProducts: this.state.selectedProducts.filter((item) => item.id !== product.id) });
      } else {
        this.setState({ selectedProducts: this.state.selectedProducts.concat(product) });
      }
    } else {
      if (getLastArrayItem<ICategoryOrProductItem>(this.state.selectedProducts)?.id === product.id) return;
      this.setState({ selectedProducts: [product] });
    }
  };

  getFetchingParams = () => {
    const params: any = {
      name: this.state.search,
      is_delete: 0,
    };

    if (!this.state.isAllStatuses) {
      params.status = 1;
    }

    if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES && this.state.categoriesWithProducts) {
      params.inc_items = 1;
      params.inc_modifiers = 1;
      params.inc_sizes = 1;
    }

    return params;
  };

  onSearch = (search: string) => {
    this.setState({ search }, () => (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? this.fetchCategory(1) : this.fetchProducts(1)));
  };

  renderContent = () => {
    if (this.state.loading && !this.state.refreshing) {
      return emptyArray().map((_, index) => <CategoryAndProductModalItem key={'key-' + String(index)} clickItem={this.changeCategory} />);
    }
    return this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES || modalStore.categoryAndProductModal?.isProducts === CATEGORY_MODAL_PRODUCT.NONE
      ? this.renderCategories()
      : this.renderProducts();
  };

  renderCategories = () => {
    if (this.noCategoriesForRender) {
      return (
        <div className="d-flex full-height full-width align-items-center">
          <p className="family-bold text-size-26 color-dark mx-auto my-auto">{localeStore.t('category_not_found')}</p>
        </div>
      );
    } else {
      return this.renderCategoriesArray.map((item: ICategoryOrProductItem) => (
        <CategoryAndProductModalItem
          item={item}
          key={item.id}
          active={this.state.isAllCategories ? true : this.state.selectedCategories.some((category) => category.id === item.id)}
          noAvailable={getUnavailable(item)}
          clickItem={this.changeCategory}
        />
      ));
    }
  };

  renderProducts = () => {
    if (this.noProductsForRender) {
      return (
        <div className="d-flex full-height full-width align-items-center">
          <p className="family-bold text-size-26 color-dark mx-auto my-auto">{localeStore.t('no_items_found')}</p>
        </div>
      );
    } else {
      return this.renderProductsArray.map((item: ICategoryOrProductItem) => (
        <CategoryAndProductModalItem
          item={item}
          key={item.id}
          active={this.state.isAllProducts ? true : this.state.selectedProducts.some((product) => product.id === item.id)}
          noAvailable={this.selectedUnavailableProducts.some((product) => product.id === item.id)}
          clickItem={this.changeProduct}
        />
      ));
    }
  };

  renderPagination = () => {
    return this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? (
      <Pagination pagination={this.state.categoriesPagination} type="arrow" onChange={this.fetchCategory} />
    ) : (
      <Pagination pagination={this.state.productsPagination} type="arrow" onChange={this.fetchProducts} />
    );
  };

  get noCategoriesForRender(): boolean {
    switch (true) {
      case !this.state.selectedCategories.length && this.state.activeCrumb === MODALS_CRUMB.SELECTED && !this.state.isAllCategories:
      case this.state.categoriesPagination.data.length === 0 && this.selectedUnavailableCategories.length === 0:
        return true;
      default:
        return false;
    }
  }

  get noProductsForRender(): boolean {
    switch (true) {
      case !this.state.selectedCategories.length && !this.state.isAllCategories:
      case this.state.productsPagination.data.length === 0 && this.selectedUnavailableProducts.length === 0:
      case !(this.state.selectedProducts.length + this.selectedUnavailableProducts.length) &&
        this.state.activeCrumb === MODALS_CRUMB.SELECTED &&
        !this.state.isAllProducts:
        return true;
      default:
        return false;
    }
  }

  get renderCategoriesArray(): ICategoryOrProductItem[] {
    if (modalStore.categoryAndProductModal?.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE) {
      return this.selectedUnavailableCategories.length && this.state.categoriesPagination.current_page === this.state.categoriesPagination.last_page
        ? [...this.state.categoriesPagination.data, ...this.selectedUnavailableCategories]
        : this.state.categoriesPagination.data;
    } else {
      return this.state.activeCrumb === MODALS_CRUMB.SELECTED
        ? this.state.selectedCategories
        : [...this.state.categoriesPagination.data, ...this.selectedUnavailableCategories];
    }
  }

  get renderProductsArray(): ICategoryOrProductItem[] {
    if (modalStore.categoryAndProductModal?.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE) {
      return this.selectedUnavailableProducts.length > 0 && this.state.productsPagination.current_page === this.state.productsPagination.last_page
        ? [...this.state.productsPagination.data, ...this.selectedUnavailableProducts]
        : this.state.productsPagination.data;
    } else {
      return this.state.activeCrumb === MODALS_CRUMB.SELECTED
        ? this.state.selectedProducts
        : [...this.state.productsPagination.data, ...this.selectedUnavailableProducts];
    }
  }

  get headerTitle(): string {
    if (modalStore.categoryAndProductModal?.headerTitle) {
      return modalStore.categoryAndProductModal.headerTitle;
    } else if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES) {
      return modalStore.categoryAndProductModal?.isCategories === CATEGORY_MODAL_CATEGORY.SINGLE ? 'select_category' : 'select_categories';
    } else {
      return modalStore.categoryAndProductModal?.isProducts === CATEGORY_MODAL_PRODUCT.SINGLE ? 'select_item' : 'select_items';
    }
  }

  get isMulty(): boolean {
    if (this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES) {
      return modalStore.categoryAndProductModal?.isCategories === CATEGORY_MODAL_CATEGORY.MULTIPLE;
    } else return modalStore.categoryAndProductModal?.isProducts === CATEGORY_MODAL_PRODUCT.MULTIPLE;
  }

  render() {
    return (
      <CustomModalComponent
        isOpen
        className={`${
          modalStore.categoryAndProductModal?.isProducts !== CATEGORY_MODAL_PRODUCT.NONE || this.isMulty
            ? 'custom-modal'
            : 'custom-modal_single-choice'
        }`}
        onRequestClose={this.close}
        withCloseIcon
      >
        <ModalBackground>
          <ModalHeader title={localeStore.t(this.headerTitle)} />
          <CategoryAndProductModalHeader
            section={this.state.section}
            activeCrumb={this.state.activeCrumb}
            isMulty={this.isMulty}
            isAll={this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? this.state.isAllCategories : this.state.isAllProducts}
            search={this.state.search}
            selectedItems={this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES ? this.state.selectedCategories : this.state.selectedProducts}
            selectedUnavailableItemsLength={
              this.state.section === CATEGORY_MODAL_SECTIONS.CATEGORIES
                ? this.selectedUnavailableCategories.length
                : this.selectedUnavailableProducts.length
            }
            isProducts={modalStore.categoryAndProductModal?.isProducts}
            isCategories={modalStore.categoryAndProductModal?.isCategories}
            loading={this.state.loading}
            clickSection={this.clickSection}
            clickCrumb={this.clickCrumb}
            clickSelectAll={this.clickSelectAll}
            clickDeselectAll={this.clickDeselectAll}
            onSearch={this.onSearch}
          />
          <div className="pos-relative d-flex flex-1 flex-column px-8">
            <ScrollableBlock showScrollbar className="d-flex flex-1" scrollClassName="full-height">
              <ul className="custom-modal__container mb-2">{this.renderContent()}</ul>
            </ScrollableBlock>
            <div className="mt-4">{this.renderPagination()}</div>
            {this.state.loading || this.state.refreshing ? <LoadingOverlay refreshing={this.state.refreshing} /> : null}
          </div>
          <ModalFooter>
            <Button title={localeStore.t('txt_save')} onClick={this.clickSave} loading={this.state.loading || this.state.refreshing} size="large" />
          </ModalFooter>
        </ModalBackground>
      </CustomModalComponent>
    );
  }
}
